<template>
    <div
        class="container mx-auto px-3 pb-32 md:px-8"
        :class="{
            'space-y-8 md:space-y-12': editing,
            '2xl:max-w-(--breakpoint-xl)': !editing,
        }"
    >
        <step-form-heading v-if="!editing" :icon="faSlidersSimple">
            New Custom Flow

            <template v-slot:description> Group a set of resources together. </template>
        </step-form-heading>

        <page-header v-else class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/automation/flows" class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Flows
                </a>
            </template>

            {{ data.name }}
        </page-header>

        <div class="grid grid-cols-12">
            <custom-flow-sidebar v-if="editing" :flowId="props.flow?.flow_id"></custom-flow-sidebar>

            <div
                v-if="loading"
                class="col-span-12 text-center py-10"
                :class="{
                    'lg:col-span-10': editing,
                }"
            >
                <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
            </div>

            <form-wrapper
                v-else
                :hideNav="editing"
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'notes', label: 'Description' },
                    { id: 'category', label: 'Category' },
                ]"
            >
                <vx-expandable-card id="name">
                    <template v-slot:header> Name </template>

                    <template v-slot:content>
                        <vx-input
                            v-model="data.name"
                            data-test="flow-name"
                            :error="displayFirstError(validation.name)"
                            autofocus
                            size="lg"
                            :maxlength="80"
                        ></vx-input>
                    </template>
                </vx-expandable-card>
                <vx-expandable-card id="notes">
                    <template v-slot:header> Description </template>

                    <template v-slot:content>
                        <vx-input
                            v-model="data.notes"
                            data-test="flow-notes"
                            :error="displayFirstError(validation.notes)"
                            autofocus
                            autoresize
                            :rows="3"
                            size="lg"
                            :maxlength="2000"
                        ></vx-input>
                    </template>
                </vx-expandable-card>

                <resource-category
                    id="category"
                    v-model="data.category"
                    @reset="data.category = null"
                ></resource-category>

                <vx-sticky-footer class="flex-col! lg:flex lg:flex-row!">
                    <vx-button
                        href="/automation/flows"
                        class="order-3 lg:w-20 lg:order-1"
                        size="lg"
                        color="muted-dark"
                        outline
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="submit"
                        :loading="processing"
                        type="button"
                        class="order-1 lg:grow lg:order-3"
                        size="lg"
                        color="primary"
                    >
                        {{ editing ? 'Update Flow' : 'Create Flow' }}
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </div>
    </div>
</template>

<script setup>
import { faArrowLeft, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxExpandableCard, VxIconLoading, VxInput, VxStickyFooter } from '@voxie/frontend-components';
import { required } from '@vuelidate/validators';
import { ref, watch, computed } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { useToasted } from '~/composables/useToasted';
import { flowsClient } from '~/services';
import { displayFirstError } from '~/utils/validation';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import CustomFlowSidebar from './CustomFlowSidebar.vue';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { faSlidersSimple } from '@fortawesome/pro-regular-svg-icons';

const toasted = useToasted();

const props = defineProps({
    flow: {
        type: [Object, undefined],
        default: undefined,
    },
});

const teamId = Spark.state.currentTeam.id;

const editing = computed(() => Boolean(props.flow?.flow_id));

const data = ref({
    flow_id: props.flow?.flow_id,
    name: '',
    notes: '',
    category: null,
});

const loading = ref(false);

const prepareForm = async () => {
    if (loading.value) {
        return;
    }

    try {
        loading.value = true;

        if (props.flow) {
            data.value.flow_id = props.flow.flow_id;
            data.value.name = props.flow.name;
            data.value.notes = props.flow.notes;
            data.value.category = props.flow.category;
        }
    } catch (e) {
        console.error(e);

        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

watch(
    () => props.flow?.id,
    async () => {
        prepareForm();
    },
    {
        immediate: true,
    }
);

const save = async () => {
    const response = await flowsClient.createOrUpdate(teamId, {
        flow_id: data.value.flow_id,
        name: data.value.name,
        notes: data.value.notes,
        category: data.value.category,
    });

    data.value.flow_id = response.data.flow_id;
    return response.data;
};

const { submit, processing, validation } = useFormSubmission({
    data,
    validation: {
        name: { required },
        notes: { required },
    },
    submit: save,
    preserveScroll: () => editing.value,
    notification: () =>
        editing.value
            ? {
                  message: 'Flow updated',
                  action: {
                      icon: faSquareArrowUpLeft,
                      label: 'Back to Flows',
                      href: '/automation/flows',
                  },
              }
            : {
                  message: 'Flow created',
                  redirect: `/automation/flows/${data.value.flow_id}/resources`,
                  action: {
                      icon: faSquareArrowUpLeft,
                      label: 'Back to Flows',
                      href: '/automation/flows',
                  },
              },
});
</script>
